<script>
import Layout from '../../layouts/main';
import PageHeader from '@/components/page-header';
import appConfig from '@/app.config';
import Content from '@/services/Content';
import Swal from 'sweetalert2';

export default {
  components: { Layout, PageHeader },

  page: {
    title: 'Video Projects',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  data() {
    return {
      title: 'Video Projects',
      items: [
        { text: 'News' },
        { text: 'Video Projects', active: true, href: '/news/video-projects' },
      ],

      isBusy: false,
      error: null,

      tableData: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: 'timestampt',
      sortDesc: true,

      fields: [
        { key: 'id',          label: 'ID',        sortable: true,  thStyle: { width: '110px' } },
        { key: 'file_name',   label: 'File',     sortable: true,  thStyle: { width: '250px' } },
        { key: 'type',        label: 'Type',     sortable: true,  thStyle: { width: '100px' } },
        { key: 'status',      label: 'Status',   sortable: true,  thStyle: { width: '130px' } },
        { key: 'timestampt', label: 'Created',  sortable: true,  thStyle: { width: '130px' } },
        { key: 'action',      label: '',         sortable: false, thStyle: { width: '60px' } },
      ],
    };
  },

  async created() {
    await this.loadProjects();
  },

  methods: {
    async loadProjects() {
      try {
        this.isBusy = true;
        this.error = null;
        const response = await Content.getVideoProjects();
        this.tableData = response.data.data || response.data || [];
        this.totalRows = this.tableData.length;
      } catch (err) {
        this.error = err?.response?.data?.error || 'Failed to load video projects.';
        this.tableData = [];
        this.totalRows = 0;
      } finally {
        this.isBusy = false;
      }
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    },

    fileNameFromKey(key) {
      if (!key) return '—';
      return key.split('/').pop();
    },

    statusVariant(status) {
      const map = {
        pending:    'warning',
        processing: 'info',
        completed:  'success',
        failed:     'danger',
      };
      return map[(status || '').toLowerCase()] || 'secondary';
    },

    deleteProject(id) {
      Swal.fire({
        title: 'Delete project?',
        text: 'This project will be permanently deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#74788d',
        confirmButtonText: 'Delete',
      }).then(async (result) => {
        if (!result.value) return;
        try {
          await Content.deleteVideoProcess(id);
          this.tableData = this.tableData.filter(r => r.id !== id);
          this.totalRows = this.tableData.length;
          Swal.fire('Deleted!', 'Project has been deleted.', 'success');
        } catch (err) {
          Swal.fire('Error', err?.response?.data?.error || 'Failed to delete project.', 'error');
        }
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">

            <!-- Top bar: count + New Project button -->
            <div class="row mb-3 align-items-center">
              <div class="col">
                <h4 class="card-title mb-0">
                  Total Projects
                  <span class="badge bg-secondary ms-1">{{ totalRows }}</span>
                </h4>
              </div>
              <div class="col-auto d-flex gap-2">
                <router-link
                    to="/videos/video-editor"
                    class="btn btn-success waves-effect waves-light"
                >
                  <i class="mdi mdi-plus me-1"></i> New Project
                </router-link>
                <button
                  class="btn btn-outline-secondary waves-effect"
                  :disabled="isBusy"
                  @click="loadProjects"
                >
                  <i class="mdi mdi-refresh me-1"></i> Refresh
                </button>
              </div>
            </div>

            <!-- Error -->
            <div v-if="error" class="alert alert-danger py-2 px-3 small mb-3">
              <i class="mdi mdi-alert-circle-outline me-1"></i>{{ error }}
            </div>

            <!-- Per-page / search controls -->
            <div class="row mb-2">
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                    ></b-form-select>
                    &nbsp;entries
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_filter text-md-end">
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ms-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive mb-0">
              <b-table
                :busy="isBusy"
                :items="tableData"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                show-empty
                empty-text="No projects found."
                @filtered="onFiltered"
              >

                <!-- ID link to editor -->
                <template v-slot:cell(id)="data">
                  <router-link
                    v-if="(data.item.status || '').toLowerCase() === 'completed'"
                    :to="{ path: '/videos/video-editor', query: { process_id: data.item.id } }"
                    class="text-primary fw-semibold"
                    v-b-tooltip.hover title="Open in editor"
                  >{{ data.item.id }}</router-link>
                  <span v-else class="text-muted">{{ data.item.id }}</span>
                </template>

                <!-- File -->
                <template v-slot:cell(file_name)="data">
                  <span class=" d-inline-block"  :title="data.item.s3_key || data.item.file_name">
                    {{ fileNameFromKey(data.item.s3_key || data.item.file_name) }}
                  </span>
                </template>

                <!-- Type -->
                <template v-slot:cell(type)="data">
                  <span
                    class="badge fs-6"
                    :class="data.item.type === 'auto' ? 'bg-primary' : 'bg-secondary'"
                  >
                    {{ data.item.type || '—' }}
                  </span>
                </template>

                <!-- Status -->
                <template v-slot:cell(status)="data">

                  <span class="badge fs-6" :class="'bg-' + statusVariant(data.item.status)">
                    {{ data.item.status || '—' }}
                  </span>
                </template>

<!--                &lt;!&ndash; Cuts &ndash;&gt;-->
<!--                <template v-slot:cell(cuts_count)="data">-->
<!--                  <span v-if="data.item.type === 'auto'" class="text-muted small">auto</span>-->
<!--                  <span v-else>{{ data.item.cuts_count != null ? data.item.cuts_count : '—' }}</span>-->
<!--                </template>-->

                <!-- Created -->
                <template v-slot:cell(timestampt)="data">
                  <span class="text-muted small">{{ formatDate(data.item.timestampt) }}</span>
                </template>


                <!-- Actions -->
                <template v-slot:cell(action)="data">
                  <ul class="list-inline mb-0">
                    <li v-if="(data.item.status || '').toLowerCase() === 'completed'" class="list-inline-item">
                      <a
                        href="javascript:void(0);"
                        class="px-2 text-danger"
                        @click="deleteProject(data.item.id)"
                      >
                        <i class="uil uil-trash-alt font-size-18"></i>
                      </a>
                    </li>
                  </ul>
                </template>

                <!-- Busy state -->
                <template #table-busy>
                  <div class="text-center text-success my-3">
                    <b-spinner class="align-middle me-2"></b-spinner>
                    <strong>Loading…</strong>
                  </div>
                </template>

              </b-table>
            </div>

            <!-- Pagination -->
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>

          </div><!-- /card-body -->
        </div><!-- /card -->
      </div>
    </div>
  </Layout>
</template>
